/**
 * A re-import's review and write, worked out on the page (decision 64; part 23b, tables CS, V′): the file's chapters
 * matched to the book's (part 23a's table MA), each matched one's merge asked for (its base, current text and the file's
 * reading, its pictures settled by table PI), each one's state and diff for the review once merged, and, from the
 * writer's takes, the write table AP makes. Pure.
 */
import { countWords, matchChapters, parseChapter, ScanBudget, settleImages, titleKey } from '@cockatrice/core';
import type { DiffLine, FlagKind, ImportFlag, MergedChapter, ReimportedChapter, ReimportedComment } from '@cockatrice/core';
import type { CommentDraft, CommentedChapter, ReimportedDraft, ReimportedText } from '@cockatrice/storage';
import type { MergeChapter } from '../docx/protocol.ts';
import type { ExportedBook } from '../shell/book-export.ts';
import { cleanTitle } from '../shell/workspace.ts';
import { anchorComments } from './comments.ts';
import type { IdentifiedComment } from './comments.ts';
import type { DecidedPicture } from './pictures.ts';
import type { ExportBase, ReimportRequest } from './reimport.types.ts';

/** A chapter of the book the file matched (MA1–MA5), with what the merge is given for it. */
interface Matched {
  readonly kind: 'matched';
  readonly id: string;
  readonly title: string;
  readonly current: string;
  /** The export base's text (CS2–CS4), null with none (CS5). */
  readonly base: string | null;
  /** The chapter's title as exported (fix-next 27, table PT), null when its base keeps none or it has no base. */
  readonly baseTitle: string | null;
  readonly merge: MergeChapter;
}

/** A file chapter that matched none (MA6, CS7): its title and text, its pictures settled. */
interface Added {
  readonly kind: 'new';
  readonly title: string;
  readonly text: string;
}

/** One file chapter as the review plans it, in the file's order. */
export interface Planned {
  readonly file: ReimportedChapter;
  readonly match: Matched | Added;
}

/** What the merge is asked and what the review then shows besides: the file's chapters planned, the book's not in the file. */
export interface Plan {
  readonly chapters: readonly Planned[];
  /** MA7, CS6: the book's live chapters no file chapter matched, by ID and title, in the book's order. */
  readonly missing: readonly { readonly id: string; readonly title: string }[];
}

/**
 * The plan for `file`'s chapters against `book`: matched by table MA; each matched one with its base from `bases` (by
 * chapter ID, resolved by the caller from the snapshot the file names, CS5 when absent) and its pictures settled by
 * `settled` (table PI).
 */
export function planReimport(file: readonly ReimportedChapter[], book: ExportedBook, bases: ReadonlyMap<string, ExportBase>, settled: ReadonlyMap<string, string>): Plan {
  const matching = matchChapters(file, book.chapters);
  // Each book chapter by the file chapter that matched it; the others are missing (MA7).
  const byFile = new Map<number, ExportedBook['chapters'][number]>();
  const missing: { readonly id: string; readonly title: string }[] = [];
  for (const chapter of book.chapters) {
    const at = matching.matches.findIndex((match) => match.kind === 'matched' && match.id === chapter.id);
    if (at === -1) missing.push({ id: chapter.id, title: chapter.title });
    else byFile.set(at, chapter);
  }
  const chapters = file.map((chapter, i): Planned => {
    const text = settleImages(chapter.text, settled);
    const stored = byFile.get(i);
    // Deep-fix r2: a title from the file is cleaned as every title the app stores is (B4, part 18's import).
    if (stored === undefined) return { file: chapter, match: { kind: 'new', title: cleanTitle(chapter.title ?? ''), text } };
    const exported = bases.get(stored.id);
    const base = exported?.text ?? null;
    return { file: chapter, match: { kind: 'matched', id: stored.id, title: stored.title, current: stored.text, base, baseTitle: exported?.title ?? null, merge: { base, current: stored.text, file: text } } };
  });
  return { chapters, missing };
}

/** The merge's chapters, in the plan's order of matched chapters. */
export function mergeJob(plan: Plan): MergeChapter[] {
  return plan.chapters.flatMap(({ match }) => (match.kind === 'matched' ? [match.merge] : []));
}

/**
 * A chapter's state in the review (CS1–CS8): nothing new in the file (CS1), changed only here since the export (CS3),
 * changed by the file (CS2, CS5), by both (CS4), or new (CS7).
 */
export type ChapterState = 'unchanged' | 'here' | 'changed' | 'both' | 'new';

/** One chapter of the review (V′5), in the file's order. */
export interface ReviewChapter {
  /** The chapter's place among the file's. */
  readonly index: number;
  /** As the book titles it (matched) or the file does (new); empty for untitled. */
  readonly title: string;
  readonly state: ChapterState;
  /** CS5: whether the chapter had an export base to merge against (always for a new one). */
  readonly base: boolean;
  /** MG4: regions the app and the file both changed, the file's taken. */
  readonly conflicts: number;
  /**
   * CS8: the file's title when it renames the chapter, else null (fix-next 27: three ways when the base keeps the title
   * exported, table CS8′).
   */
  readonly rename: string | null;
  /**
   * CS8′: whether the rename starts ticked: the file renamed the chapter and the app didn't since the export. Unticked
   * when both did, or when the base keeps no title to tell (deep-fix r4's rule).
   */
  readonly renameTaken: boolean;
  /** The chapter's words once taken. */
  readonly words: number;
  /** Current to taken, block by block; the rest counted. */
  readonly diff: readonly DiffLine[];
  readonly more: number;
  /** Whether taking it changes anything (CS1, CS3: nothing to take, CS15). */
  readonly takeable: boolean;
  /** The flags the reader raised in this chapter (CS13: `tracked`). */
  readonly flags: readonly FlagKind[];
}

/** V″1 (part 23c): the file's comments placed in a chapter, and how many of them are in new chapters (kept only if taken). */
export interface ReviewComments {
  readonly total: number;
  readonly inNew: number;
}

/** The review (V′5): every file chapter, the book's chapters missing from the file, the comments, the flags. */
export interface Review {
  readonly chapters: readonly ReviewChapter[];
  readonly missing: readonly { readonly id: string; readonly title: string }[];
  readonly comments: ReviewComments;
  readonly flags: readonly ImportFlag[];
}

/** The plan's chapters with their merges (one per matched chapter, in order), each with its review row. */
interface Reviewed {
  readonly planned: Planned;
  readonly merged: MergedChapter | null;
  readonly row: ReviewChapter;
}

/**
 * CS8: the file's title, cleaned as the app cleans every title it stores (deep-fix r2), when its key isn't the chapter's
 * (curly quotes are no rename, LO1); null for none, an untitled one or one that cleans to nothing (CS9). Table CS8′
 * (fix-next 27), with the title exported E, the chapter's A and the file's F, compared by key: F as E, the editor
 * renamed nothing, so no offer (CS8′1); F not E and A as E, the editor's rename, ticked (CS8′2); all three apart, both
 * renamed it, unticked (CS8′3); no E, unticked, as before (CS8′4).
 */
function renameOf(planned: Planned): { readonly title: string; readonly taken: boolean } | null {
  const title = cleanTitle(planned.file.title ?? '');
  if (title === '' || planned.match.kind !== 'matched' || titleKey(title) === titleKey(planned.match.title)) return null;
  const exported = planned.match.baseTitle;
  if (exported === null) return { title, taken: false };
  if (titleKey(title) === titleKey(exported)) return null;
  return { title, taken: titleKey(planned.match.title) === titleKey(exported) };
}

/**
 * Each planned chapter with its merge and review row; `merged` holds one merge per matched chapter, in order (a matched
 * chapter past its end has none, and shows as unchanged).
 */
function reviewed(plan: Plan, merged: readonly MergedChapter[], flags: readonly ImportFlag[]): Reviewed[] {
  let next = 0;
  return plan.chapters.map((planned, index): Reviewed => {
    const kinds = flags.filter((flag) => flag.chapters.includes(index)).map((flag) => flag.kind);
    const { match } = planned;
    if (match.kind === 'new') {
      const row: ReviewChapter = { index, title: match.title, state: 'new', base: true, conflicts: 0, rename: null, renameTaken: false, words: planned.file.words, diff: [], more: 0, takeable: true, flags: kinds };
      return { planned, merged: null, row };
    }
    const result = merged[next] ?? null;
    next += 1;
    const rename = renameOf(planned);
    const here = match.base !== null && match.current !== match.base;
    const text = result?.changed === true ? result.text : match.current;
    const changed = result?.changed === true;
    const state: ChapterState = changed ? (here ? 'both' : 'changed') : here ? 'here' : 'unchanged';
    const row: ReviewChapter = {
      index,
      title: match.title,
      state,
      base: match.base !== null,
      conflicts: result?.conflicts ?? 0,
      rename: rename?.title ?? null,
      renameTaken: rename?.taken === true,
      words: countWords(parseChapter(text)),
      diff: result?.diff ?? [],
      more: result?.more ?? 0,
      takeable: changed,
      flags: kinds,
    };
    return { planned, merged: result, row };
  });
}

/** V′5, V″1: the review of `plan` once its matched chapters are `merged` (in order), with the file's `comments` and the reader's `flags`. */
export function reviewOf(plan: Plan, merged: readonly MergedChapter[], flags: readonly ImportFlag[], comments: readonly ReimportedComment[] = []): Review {
  const inNew = comments.filter((comment) => plan.chapters[comment.chapter]?.match.kind === 'new').length;
  return { chapters: reviewed(plan, merged, flags).map(({ row }) => row), missing: plan.missing, comments: { total: comments.length, inNew }, flags };
}

/**
 * What Apply writes (table AP), the new pictures it needs first (PI5, PI9: those its texts name, by placeholder), and how
 * many comments it leaves out for being in a new chapter not taken (AC3).
 */
export interface Write {
  readonly request: ReimportRequest;
  readonly pictures: readonly { readonly key: string; readonly file: Blob }[];
  readonly commentsLeft: number;
}

/**
 * Table AP from the writer's takes (`take[i]` for file chapter i, CS14: a skipped one gives nothing; `titles[i]` for its
 * file title, CS8): each taken chapter's text as reviewed and as merged, its title as reviewed and the file's if taken, a
 * conflict copy when it changed in the app since the export
 * or had no base (L9, CS4, CS5) and its text changes; each taken new chapter after the chapter its nearest matched
 * predecessor in the file is (CS7b), or first; and the new pictures the taken texts name.
 *
 * Part 23c (tables AN, AC): each of `comments` anchored in its chapter's text as the write leaves it (the merged text of a
 * taken chapter, a new chapter's text, else the current text, AC4) and asked with it; a comment of a new chapter not
 * taken is left out (AC3).
 */
export function writeOf(bookId: string, plan: Plan, merged: readonly MergedChapter[], take: readonly boolean[], pictures: readonly DecidedPicture[], titles: readonly boolean[] = [], comments: readonly IdentifiedComment[] = []): Write {
  const taken: ReimportedText[] = [];
  const added: ReimportedDraft[] = [];
  const commented: CommentedChapter[] = [];
  const budget = new ScanBudget();
  // Each file chapter's comments, by its index.
  const byChapter = new Map<number, IdentifiedComment[]>();
  for (const comment of comments) {
    const own = byChapter.get(comment.source.chapter);
    if (own === undefined) byChapter.set(comment.source.chapter, [comment]);
    else own.push(comment);
  }
  const drafts = (index: number, text: string): { readonly comments?: CommentDraft[] } => {
    const own = byChapter.get(index);
    byChapter.delete(index);
    return own === undefined ? {} : { comments: anchorComments(text, own, budget) };
  };
  let anchor: string | null = null;
  for (const { planned, merged: result, row } of reviewed(plan, merged, [])) {
    const { match } = planned;
    const chosen = row.takeable && take[row.index] === true;
    if (match.kind === 'matched') {
      anchor = match.id;
      const title = titles[row.index] === true ? row.rename : null;
      if (!chosen && title === null) {
        const own = drafts(row.index, match.current).comments;
        if (own !== undefined) commented.push({ id: match.id, text: match.current, comments: own });
        continue;
      }
      const after = chosen && result?.changed === true ? result.text : match.current;
      taken.push({ id: match.id, before: match.current, after, title, titleBefore: match.title, copy: after !== match.current && (match.base === null || match.current !== match.base), ...drafts(row.index, after) });
    } else if (chosen) added.push({ title: match.title, text: match.text, after: anchor, ...drafts(row.index, match.text) });
  }
  const texts = [...taken.map((chapter) => chapter.after), ...added.map((chapter) => chapter.text)];
  return {
    request: { bookId, taken, added, commented },
    pictures: pictures.flatMap(({ picture, decision }) => (decision.kind === 'new' && texts.some((text) => text.includes(picture.key)) ? [{ key: picture.key, file: decision.file }] : [])),
    // AC3: what no chapter took.
    commentsLeft: [...byChapter.values()].reduce((sum, own) => sum + own.length, 0),
  };
}
